import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../auth/firebaseAuth';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import ToggleSwitch from '../../components/common/ToggleSwitch';
import LandlordToolkitLayout from '../../components/landlord-toolkit/LandlordToolkitLayout';

interface ReminderSettings {
  enabled: boolean;
  lateRentEnabled: boolean;
  rtbEnabled: boolean;
  reviewEnabled: boolean;
}

const ReminderSettingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState<ReminderSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      try {
        const token = await currentUser.getIdToken();
        const res = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getReminderSettings`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to load');
        setSettings(json.data);
      } catch (e: any) {
        setError(e.message);
      }
    };
    load();
  }, [currentUser]);

  const update = async () => {
    if (!currentUser || !settings) return;
    setSaving(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/updateReminderSettings`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(settings)
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to save');
      setSettings(json.data);
      toast.success('Reminder settings saved');
    } catch (e: any) {
      setError(e.message);
      toast.error(e.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <LandlordToolkitLayout title="Automated Reminders">
        <div className="max-w-3xl mx-auto p-6">
          <div className="h-8 w-32 bg-gray-200 rounded mb-6 animate-pulse" />
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 space-y-6">
            <div className="h-6 w-1/4 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
            <hr />
            <div className="h-20 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      </LandlordToolkitLayout>
    );
  }

  return (
    <LandlordToolkitLayout title="Automated Reminders">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6">
            <p className="text-gray-500 text-sm mb-6">
              Control which automated emails RentSmart sends to your tenants.
              Disabling the master switch stops all notifications.
            </p>

            {error && (
              <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100">
                {error}
              </div>
            )}

            {/* Master Switch */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 mb-6">
              <ToggleSwitch
                label="Enable All Reminders"
                description="Turn off to temporarily disable all automated emails."
                checked={settings.enabled}
                onChange={(checked) =>
                  setSettings({ ...settings, enabled: checked })
                }
              />
            </div>

            <div className="border-t border-gray-100 my-4" />

            {/* Sub Settings Grid */}
            <div className="grid gap-x-12 gap-y-2">
              <ToggleSwitch
                label="Late Rent Reminders"
                description="Emails sent when rent is overdue or coming due."
                checked={settings.lateRentEnabled}
                disabled={!settings.enabled}
                onChange={(checked) =>
                  setSettings({ ...settings, lateRentEnabled: checked })
                }
              />

              <div className="border-t border-gray-100" />

              <ToggleSwitch
                label="RTB Registration"
                description="Annual RTB registration due alerts."
                checked={settings.rtbEnabled}
                disabled={!settings.enabled}
                onChange={(checked) =>
                  setSettings({ ...settings, rtbEnabled: checked })
                }
              />

              <div className="border-t border-gray-100" />

              <ToggleSwitch
                label="Rent Reviews"
                description="Notifications when a tenancy reaches review period."
                checked={settings.reviewEnabled}
                disabled={!settings.enabled}
                onChange={(checked) =>
                  setSettings({ ...settings, reviewEnabled: checked })
                }
              />
            </div>
          </div>

          {/* Action Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={update}
              disabled={saving}
              className={`
              inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
              ${saving ? 'bg-indigo-400 cursor-wait' : 'bg-indigo-600 hover:bg-indigo-700'}
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors
            `}
            >
              {saving ? 'Saving Changes...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </LandlordToolkitLayout>
  );
};

export default ReminderSettingsPage;
